import React from "react";
import { Link } from "react-router-dom";
import SeoHead from "../../components/seo/SeoHead";
import SeoMarketingShell from "../../components/seo/SeoMarketingShell";

const faqItems = [
  {
    q: "Is this only for luxury homes?",
    a: "No. Pre-arrival groceries work for any property tier. The key is consistent execution.",
  },
  {
    q: "Do guests have to shop themselves?",
    a: "Guests browse and order from over 20,000 products through a simple pre-arrival shopping experience designed for speed and clarity.",
  },
  {
    q: "Do you stock and organize?",
    a: "Yes. Bellhopt Concierge stocks and organizes when access is provided.",
  },
  {
    q: "What is the minimum lead time?",
    a: "24 hours.",
  },
  {
    q: "Can property managers roll this out across a portfolio?",
    a: "Yes. Bellhopt is built for portfolio deployment, including enterprise portfolios of 1,000+ homes.",
  },
];

const GLASS_CARD =
  "rounded-3xl border border-[#E5E7EB] bg-white/95 backdrop-blur-2xl shadow-[0_24px_80px_rgba(15,23,42,0.12)]";
const GLASS_CARD_SOFT =
  "rounded-3xl border border-[#E5E7EB] bg-white/92 backdrop-blur-2xl shadow-[0_18px_60px_rgba(15,23,42,0.10)]";
const PRIMARY_CTA =
  "rounded-full px-7 py-3 text-white text-[12px] font-semibold tracking-[0.12em] uppercase bg-gradient-to-r from-[#FF4D4D] via-[#FF6B6B] to-[#FF4D4D] shadow-[0_14px_40px_rgba(248,113,113,0.45)] hover:shadow-[0_18px_54px_rgba(248,113,113,0.55)] transition-all";
const SECONDARY_CTA =
  "rounded-full px-7 py-3 text-[#111827] text-[12px] font-semibold tracking-[0.08em] uppercase border border-[#E5E7EB] bg-white hover:bg-[#F9FAFB] transition-all";

function Section({ id, title, children }) {
  return (
    <section id={id} className={`${GLASS_CARD_SOFT} p-7 mobile:p-5`}>
      <h2 className="font-segoe font-semibold text-[30px] mobile:text-[24px] text-[#111827]">{title}</h2>
      <div className="mt-4 text-[18px] mobile:text-[16px] text-[#6B7280] leading-8">{children}</div>
    </section>
  );
}

function PreArrivalGroceriesPage() {
  const title = "Pre-Arrival Groceries For Vacation Rentals | Bellhopt Concierge";
  const description =
    "Let guests shop before they arrive. Bellhopt Concierge delivers, stocks, and organizes groceries so the home is ready from the first hour. 24-hour lead time required.";
  const canonical = "https://bellhopt.com/pre-arrival-groceries/";

  const structuredData = [
    {
      "@context": "https://schema.org",
      "@type": "WebPage",
      name: "Pre-Arrival Groceries That Make A Stay Feel Effortless",
      url: canonical,
      description,
    },
    {
      "@context": "https://schema.org",
      "@type": "FAQPage",
      mainEntity: faqItems.map((item) => ({
        "@type": "Question",
        name: item.q,
        acceptedAnswer: { "@type": "Answer", text: item.a },
      })),
    },
    {
      "@context": "https://schema.org",
      "@type": "BreadcrumbList",
      itemListElement: [
        { "@type": "ListItem", position: 1, name: "Home", item: "https://bellhopt.com/" },
        { "@type": "ListItem", position: 2, name: "Pre-Arrival Groceries", item: canonical },
      ],
    },
  ];

  return (
    <SeoMarketingShell>
      <SeoHead
        title={title}
        description={description}
        canonical="/pre-arrival-groceries/"
        robots="index,follow"
        ogTitle="Pre-Arrival Groceries, Arrival-Ready"
        ogDescription="Groceries delivered, stocked, and organized by Bellhopt Concierge before check-in. A premium arrival standard for STRs. 24-hour lead time."
        structuredData={structuredData}
      />

      <main className="w-[90%] max-w-[1100px] m-auto pt-24 pb-10 mobile:w-[95%]">
        <section className={`${GLASS_CARD} p-8 mobile:p-5`}>
          <h1 className="font-segoe font-semibold text-[42px] mobile:text-[30px] text-[#111827] leading-tight">
            Pre-Arrival Groceries That Make A Stay Feel Effortless
          </h1>
          <p className="mt-4 text-[20px] mobile:text-[17px] text-[#6B7280] leading-8">
            Pre-arrival groceries are the fastest way to upgrade guest experience. Bellhopt Concierge delivers, stocks, and organizes groceries so guests walk into comfort, not errands.
          </p>
          <div className="mt-7 flex flex-wrap gap-3">
            <a href="https://host.bellhopt.com/signup" className={PRIMARY_CTA}>Get Started With Bellhopt</a>
            <Link to="/how-it-works" className={SECONDARY_CTA}>How It Works</Link>
          </div>
          <p className="mt-4 text-[13px] text-[#6B7280]">
            24-hour lead time required. Built for any size portfolio, including 1,000+ homes.
          </p>
        </section>

        <div className="mt-6 space-y-6">
          <Section id="what-are" title="What Are Pre-Arrival Groceries">
            <p>Pre-arrival groceries give guests access to a full shopping experience with over 20,000 products before they check in. Whatever they want is already in the home when they arrive.</p>
            <p className="mt-4">This matters because the first hour sets the tone:</p>
            <ul className="list-disc pl-6 mt-2">
              <li>Less friction after travel</li>
              <li>Fewer urgent guest messages</li>
              <li>A more premium first impression</li>
              <li>Better reviews when the stay feels prepared</li>
            </ul>
          </Section>

          <Section id="why-guests-care" title="Why Guests Care More Now">
            <p>Guest expectations have evolved. It is no longer enough for a place to be clean and spacious.</p>
            <p className="mt-4">Guests increasingly choose between:</p>
            <ul className="list-disc pl-6 mt-2">
              <li>hotels vs short-term rentals</li>
              <li>one short-term rental vs another in the same area</li>
            </ul>
            <p className="mt-4">
              Comfort and convenience are becoming deciding factors. Pre-arrival groceries are one of the highest perceived-value upgrades because it immediately reduces effort for the guest.
            </p>
          </Section>

          <Section id="what-bellhopt-delivers" title="What Bellhopt Concierge Delivers">
            <p>Bellhopt Concierge is built to execute, not just drop off.</p>
            <ul className="list-disc pl-6 mt-2">
              <li>Delivery to the property</li>
              <li>In-home stocking when access is provided</li>
              <li>Basic organization that looks intentional and premium</li>
            </ul>
            <p className="mt-4">This is the difference between "groceries arrived" and "the home was prepared."</p>
          </Section>

          <section className={`${GLASS_CARD_SOFT} p-7 mobile:p-5`}>
            <h2 className="font-segoe font-semibold text-[30px] mobile:text-[24px] text-[#111827]">
              Make Arrival Effortless
            </h2>
            <p className="mt-4 text-[18px] mobile:text-[16px] text-[#6B7280] leading-8">
              Pre-arrival groceries, stocked and organized by Bellhopt Concierge. 24-hour lead time required.
            </p>
            <div className="mt-6 flex flex-wrap gap-3">
              <a href="https://host.bellhopt.com/signup" className={PRIMARY_CTA}>Get Started With Bellhopt</a>
              <Link to="/how-it-works" className={SECONDARY_CTA}>How It Works</Link>
            </div>
          </section>

          <Section id="who-this-is-for" title="Who This Is For">
            <p className="font-segoe font-semibold text-[#111827]">Guests:</p>
            <ul className="list-disc pl-6 mt-2">
              <li>Families arriving late</li>
              <li>Longer stays that need a full grocery run</li>
              <li>Premium travelers who value convenience</li>
            </ul>
            <p className="font-segoe font-semibold text-[#111827] mt-5">Hosts and property managers:</p>
            <ul className="list-disc pl-6 mt-2">
              <li>Anyone who wants a consistent arrival-ready standard</li>
              <li>Portfolios that need repeatable execution across markets</li>
            </ul>
          </Section>

          <Section id="lead-time" title="The 24-Hour Lead Time Policy">
            <p>Bellhopt requires a minimum 24-hour lead time to protect reliability and quality.</p>
          </Section>

          <Section id="faq" title="FAQ">
            <div className="space-y-3">
              {faqItems.map((item) => (
                <details key={item.q} className="rounded-2xl border border-[#E5E7EB] p-4 bg-white/90 open:bg-[#F9FAFB]">
                  <summary className="cursor-pointer font-segoe font-semibold text-[18px] text-[#111827]">{item.q}</summary>
                  <p className="mt-3 text-[16px] text-[#6B7280] leading-7">{item.a}</p>
                </details>
              ))}
            </div>
          </Section>

          <section className={`${GLASS_CARD_SOFT} p-7 mobile:p-5`}>
            <h2 className="font-segoe font-semibold text-[30px] mobile:text-[24px] text-[#111827]">Related Pages</h2>
            <ul className="mt-4 list-disc pl-6 text-[18px] mobile:text-[16px] text-[#6B7280] leading-8">
              <li><Link className="text-primary" to="/vacation-rental-grocery-delivery">Vacation rental grocery delivery</Link></li>
              <li><Link className="text-primary" to="/airbnb-grocery-stocking">Airbnb grocery stocking</Link></li>
              <li><Link className="text-primary" to="/for/property-managers">For property managers</Link></li>
              <li><Link className="text-primary" to="/how-it-works">How it works</Link></li>
              <li><Link className="text-primary" to="/pricing">Pricing</Link></li>
              <li><Link className="text-primary" to="/integrations">Integrations</Link></li>
            </ul>
          </section>
        </div>
      </main>
    </SeoMarketingShell>
  );
}

export default PreArrivalGroceriesPage;
